import { createSlice, createSelector } from "@reduxjs/toolkit";

import {
    homePath,
    photosLandingPath,
    ICON_LANDING_PAGE,
    PHOTO_UPLOAD,
    NOUN_PRO_EDU_CHECKOUT,
    MOD_PHOTOS
} from "data/links";

///////////////////////////
//   reducer & actions   //
///////////////////////////

export const ICONS_SEARCH_MODE = "Icons";
export const PHOTOS_SEARCH_MODE = "Photos";

const initialState = {
    visibleHeader: true,
    visibleFooter: true,
    darkModeHeader: false,
    darkModeContent: false,
    flexLayout: true,
    searchMode: ICONS_SEARCH_MODE
};

const layout = createSlice({
    name: "layout",
    initialState,
    reducers: {
        setHeaderVisibility(state, action) {
            state.visibleHeader = !!action.payload;
        },
        setFooterVisibility(state, action) {
            state.visibleFooter = !!action.payload;
        },
        setDarkHeader(state, action) {
            state.darkModeHeader = !!action.payload;
        },
        setDarkBackground(state, action) {
            state.darkModeContent = !!action.payload;
        },
        setFlexLayout(state, action) {
            state.flexLayout = !!action.payload;
        },
        setSearchMode(state, action) {
            state.searchMode =
                action.payload === PHOTOS_SEARCH_MODE
                    ? PHOTOS_SEARCH_MODE
                    : ICONS_SEARCH_MODE;
        }
    }
});

export default layout.reducer;

export const {
    setHeaderVisibility,
    setFooterVisibility,
    setDarkHeader,
    setDarkBackground,
    setFlexLayout,
    setSearchMode
} = layout.actions;

////////////////////////////
//   selector-functions   //
////////////////////////////

// NOTE: avoid using `createSelector` because the returned value is cached.
// On non-SSR pages, cached values will stick while navigating to other pages.
// Once the legacy support is removed, we can use `createSelector`.

export const getHeaderVisibility = state => {
    // legacy page support
    if (state.layout === undefined) {
        const path = window.location.pathname;
        return !(
            path.startsWith("/checkout/photo/") ||
            path.startsWith("/checkout/icon/") ||
            path.startsWith(MOD_PHOTOS)
        );
    }
    return state.layout.visibleHeader;
};

export const getFooterVisibility = state => {
    // legacy page support
    if (state.layout === undefined) {
        const path = window.location.pathname;
        return !(path.startsWith(PHOTO_UPLOAD) || path.startsWith(MOD_PHOTOS));
    }
    return state.layout.visibleFooter;
};

export const getDarkHeader = state => {
    // legacy page support
    if (state.layout === undefined) {
        return false;
    }
    return state.layout.darkModeHeader;
};

export const getDarkBackground = state => {
    // legacy page support
    if (state.layout === undefined) {
        const path = window.location.pathname;
        return (
            path.startsWith("/checkout/photo/") ||
            path.startsWith("/checkout/icon/") ||
            path.startsWith("/checkout/subscription/individual/payment/") ||
            path === NOUN_PRO_EDU_CHECKOUT ||
            path.startsWith("/checkout/subscription/team/payment/")
        );
    }
    return state.layout.darkModeContent;
};

export const getFlexLayout = state => {
    // legacy page support
    if (state.layout === undefined) {
        return true;
    }
    return state.layout.flexLayout;
};

export const getSearchMode = state => {
    // legacy page support
    if (state.layout === undefined) {
        // TODO:
        // As we SSR pages, clean up this conditional
        const path = window.location.pathname;
        if (
            path.startsWith("/photo/") || // photo detail page
            path.startsWith("/browse/photos/term/") ||
            path.startsWith("/browse/photo-shoot/") ||
            (path.startsWith("/browse/creator/") &&
                path.endsWith("/photos/")) ||
            path.startsWith("/browse/similar/photos/") ||
            path.startsWith("/browse/campaign/") ||
            path.startsWith("/search/photos/")
        ) {
            return PHOTOS_SEARCH_MODE;
        }
        return ICONS_SEARCH_MODE;
    }

    return state.layout.searchMode;
};
